import React, { useState, useEffect, createContext, useContext } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

// Toast Context for global toast management
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Toast types
const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Individual Toast Component
const Toast = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Trigger enter animation
    setIsVisible(true);

    // Auto remove after duration
    if (toast.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleRemove = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300);
  };

  const getToastStyles = () => {
    const baseStyles = "flex items-start gap-3 p-4 rounded-lg shadow-lg border min-w-[300px] max-w-[500px] transition-all duration-300";
    
    switch (toast.type) {
      case TOAST_TYPES.SUCCESS:
        return `${baseStyles} bg-green-50 border-green-200 text-green-800`;
      case TOAST_TYPES.ERROR:
        return `${baseStyles} bg-red-50 border-red-200 text-red-800`;
      case TOAST_TYPES.WARNING:
        return `${baseStyles} bg-yellow-50 border-yellow-200 text-yellow-800`;
      case TOAST_TYPES.INFO:
      default:
        return `${baseStyles} bg-blue-50 border-blue-200 text-blue-800`;
    }
  };

  const getIcon = () => {
    const iconClass = "w-5 h-5 flex-shrink-0 mt-0.5";
    
    switch (toast.type) {
      case TOAST_TYPES.SUCCESS:
        return <CheckCircle className={`${iconClass} text-green-600`} />;
      case TOAST_TYPES.ERROR:
        return <AlertCircle className={`${iconClass} text-red-600`} />;
      case TOAST_TYPES.WARNING:
        return <AlertTriangle className={`${iconClass} text-yellow-600`} />;
      case TOAST_TYPES.INFO:
      default:
        return <Info className={`${iconClass} text-blue-600`} />;
    }
  };

  const transformStyles = isExiting 
    ? 'translate-x-full opacity-0' 
    : isVisible 
      ? 'translate-x-0 opacity-100' 
      : 'translate-x-full opacity-0';

  return (
    <div
      className={`${getToastStyles()} transform ${transformStyles}`}
      role="alert"
    >
      {getIcon()}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <h4 className="font-semibold text-sm mb-1">{toast.title}</h4>
        )}
        <p className="text-sm leading-relaxed">{toast.message}</p>
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className="mt-2 text-xs font-medium underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current rounded"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={handleRemove}
        className="p-1 rounded hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current transition-colors"
        aria-label="Close notification"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (toast) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = {
      id,
      type: TOAST_TYPES.INFO,
      duration: 5000,
      ...toast
    };

    setToasts(current => [...current, newToast]);
    return id;
  };

  const removeToast = (id) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  };

  const removeAllToasts = () => {
    setToasts([]);
  };

  // Convenience methods
  const toast = {
    success: (message, options = {}) => addToast({ 
      type: TOAST_TYPES.SUCCESS, 
      message, 
      ...options 
    }),
    error: (message, options = {}) => addToast({ 
      type: TOAST_TYPES.ERROR, 
      message, 
      duration: 7000, // Longer duration for errors
      ...options 
    }),
    warning: (message, options = {}) => addToast({ 
      type: TOAST_TYPES.WARNING, 
      message, 
      ...options 
    }),
    info: (message, options = {}) => addToast({ 
      type: TOAST_TYPES.INFO, 
      message, 
      ...options 
    }),
    // Special audit-related toasts
    auditStarted: (formTitle) => addToast({
      type: TOAST_TYPES.SUCCESS,
      title: 'Audit Started',
      message: `Started new audit for "${formTitle}"`,
      duration: 4000
    }),
    auditResumed: (formTitle) => addToast({
      type: TOAST_TYPES.INFO,
      title: 'Audit Resumed',
      message: `Resumed draft audit for "${formTitle}"`,
      duration: 4000
    }),
    auditSaved: () => addToast({
      type: TOAST_TYPES.SUCCESS,
      message: 'Audit progress saved',
      duration: 3000
    }),
    auditCompleted: (score) => addToast({
      type: TOAST_TYPES.SUCCESS,
      title: 'Audit Completed',
      message: `Audit completed with score: ${score}`,
      duration: 6000
    })
  };

  const contextValue = {
    toasts,
    toast,
    addToast,
    removeToast,
    removeAllToasts
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

export { TOAST_TYPES };